import React, { useState, useMemo } from "react";
import axios from "axios";
import { FiSave } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Editor } from "@tinymce/tinymce-react";
import { useNavigate } from "react-router-dom";
import useUserLanguage from "../../hooks/useUserLanguage";
import useTranslations from "../../hooks/useTranslations";

const VersionCreate = () => {
  /* ---------- state ---------- */
  const [form, setForm] = useState({ title: "", content: "", status: "active" });
  const [saving, setSaving] = useState(false);

  /* ---------- i18n ---------- */
  const lang = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = useMemo(() => (k, d = "") => translations[k] || d, [translations]);

  /* ---------- helpers ---------- */
  const navigate = useNavigate();
  const onInput        = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const onEditorChange = c => setForm(p => ({ ...p, content: c }));

  /* ---------- kayıt ---------- */
  const saveVersion = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.warn(t("WARNING_REQUIRED_FIELDS", "Başlık ve içerik zorunlu"));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/versions`, form, {
        headers: {
          Authorization : `Bearer ${localStorage.getItem("admin_token")}`,
          "Content-Type": "application/json",
        },
      });
      toast.success(t("SUCCESS_TEMPLATE_SAVED", "Versiyon kaydedildi"));
      // navigate("/versions/list");  // istersen kayıttan sonra listeye dön
    } catch (err) {
      console.error(err);
      toast.error(t("ERROR_TEMPLATE_NOT_ADDED", "Kayıt başarısız"));
    } finally {
      setSaving(false);
    }
  };

  /* ---------- TinyMCE görsel yükleyici ---------- */
  const imageUploadHandler = async (blobInfo, success, failure, progress) => {
    try {
      const fd = new FormData();
      fd.append("file", blobInfo.blob(), blobInfo.filename());

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/versions/upload`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          },
          onUploadProgress: e => progress((e.loaded / e.total) * 100),
        }
      );

      /* TinyMCE’ye relative yolu bildiriyoruz ->  ../../uploads/...   */
      success(`../../${data.location}`);
    } catch (e) {
      console.error("Görsel yükleme hatası:", e);
      failure("Yüklenemedi");
    }
  };

  /* ---------- UI ---------- */
  return (
    <div className="container mt-4">
      <ToastContainer />
      <div className="card card-body mb-4">
        <h3 className="fw-bold text-primary mb-3">
          {t("CREATE_VERSION_TITLE", "Yeni Versiyon Oluştur")}
        </h3>

        {/* Başlık */}
        <div className="mb-3">
          <label className="form-label fw-bold">
            {t("VERSION_TITLE_LABEL", "Başlık")}
          </label>
          <input
            type="text"
            name="title"
            className="form-control"
            value={form.title}
            onChange={onInput}
            placeholder={t("PLACEHOLDER_VERSION_TITLE", "Versiyon başlığı")}
          />
        </div>

        {/* İçerik */}
        <div className="mb-3">
          <label className="form-label fw-bold">
            {t("VERSION_CONTENT_LABEL", "İçerik")}
          </label>
          <Editor
            tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
            value={form.content}
            onEditorChange={onEditorChange}
            init={{
              promotion: false,
              height: 500,
              plugins: [
                "advlist", "autolink", "link", "image", "lists", "charmap",
                "preview", "anchor", "pagebreak", "searchreplace", "wordcount",
                "visualblocks", "code", "fullscreen", "insertdatetime", "media",
                "table", "emoticons", "help",
              ],
              toolbar:
                "undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify | " +
                "bullist numlist outdent indent | link image | " +
                "print preview media fullscreen code | forecolor backcolor emoticons | help",
              content_style: "body { font-family:Helvetica,Arial,sans-serif; font-size:16px }",
              image_advtab: true,
              automatic_uploads: false,          // <- handler ile biz yönetiyoruz
              images_upload_handler: imageUploadHandler,
            }}
          />
        </div>

        {/* Kaydet */}
        <div className="d-flex justify-content-center">
          <button className="btn btn-primary" onClick={saveVersion} disabled={saving}>
            <FiSave className="me-2" />
            {saving ? t("SAVING", "Kaydediliyor…") : t("SAVE_BTN", "Kaydet")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionCreate;
